import * as _ from 'lodash-es';
import { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { context, propagation, Span, SpanKind, SpanStatusCode, trace } from '@opentelemetry/api';
import {
  ATTR_ERROR_TYPE,
  ATTR_HTTP_REQUEST_METHOD,
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_NETWORK_PEER_ADDRESS,
  ATTR_NETWORK_PEER_PORT,
  ATTR_SERVER_ADDRESS,
  ATTR_SERVER_PORT,
  ATTR_URL_FULL,
  ATTR_URL_PATH,
  ATTR_URL_SCHEME,
  ATTR_USER_AGENT_ORIGINAL
} from '@opentelemetry/semantic-conventions';
import { getPageContext, acquirePageRef, releasePageRef } from './router';
import { apiErrorCounter, httpClientRequestDuration } from './metrics';
import { serviceVersion } from './version';

interface AxiosConfigWithSpan extends InternalAxiosRequestConfig {
  _otSpan?: Span;
  _otContext?: ReturnType<typeof context.active>;
  _otStartTime?: number;
  _otPageRef?: Span | null;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMERIC_ID_RE = /\/\d+(?=\/|$)/g;

const parameterizePath = (path: string): string => {
  return _.replace(_.replace(path, UUID_RE, ':id'), NUMERIC_ID_RE, '/:id');
};

const resolveUrl = (url: string | undefined, baseURL: string | undefined): URL => {
  const base = baseURL && _.startsWith(baseURL, 'http') ? baseURL : `${window.location.origin}${baseURL || '/'}`;
  return new URL(url || '', base);
};

export const instrumentAxios = (instance: AxiosInstance) => {
  const tracer = trace.getTracer('axios', serviceVersion);

  instance.interceptors.request.use((config: AxiosConfigWithSpan) => {
    const url = resolveUrl(config.url, config.baseURL);
    const method = _.toUpper(config.method) || 'GET';
    const port = url.port ? _.parseInt(url.port) : url.protocol === 'https:' ? 443 : 80;

    // Parent under the active page span (if any) for nested tracing
    const parentContext = getPageContext();

    const route = parameterizePath(_.replace(decodeURIComponent(url.pathname), /\+/g, ''));

    const span = tracer.startSpan(
      `${method} ${route}`,
      {
        kind: SpanKind.CLIENT,
        attributes: {
          [ATTR_HTTP_REQUEST_METHOD]: method,
          [ATTR_URL_FULL]: url.toString(),
          [ATTR_URL_PATH]: url.pathname + url.search,
          [ATTR_URL_SCHEME]: _.replace(url.protocol, ':', ''),
          [ATTR_SERVER_ADDRESS]: url.hostname,
          [ATTR_SERVER_PORT]: port,
          [ATTR_NETWORK_PEER_ADDRESS]: url.hostname,
          [ATTR_NETWORK_PEER_PORT]: port,
          [ATTR_USER_AGENT_ORIGINAL]: navigator.userAgent
        }
      },
      parentContext
    );

    // Create context with the span and inject trace headers
    const ctx = trace.setSpan(parentContext, span);
    propagation.inject(ctx, config.headers);

    // Store span, context, and start time for response handling
    config._otSpan = span;
    config._otContext = ctx;
    config._otStartTime = performance.now();
    config._otPageRef = acquirePageRef();

    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const config = response.config as AxiosConfigWithSpan;
      const span = config._otSpan;

      const method = _.toUpper(response.config.method) || 'GET';
      const urlPath = resolveUrl(response.config.url, response.config.baseURL).pathname;
      const statusCode = response.status;

      if (span) {
        span.setAttribute(ATTR_HTTP_RESPONSE_STATUS_CODE, statusCode);

        if (statusCode >= 500) {
          span.setStatus({ code: SpanStatusCode.ERROR });
          apiErrorCounter?.add(1, {
            'http.request.method': method,
            'http.response.status_code': statusCode,
            'url.path': urlPath
          });
        } else {
          span.setStatus({ code: SpanStatusCode.OK });
        }

        span.end();
      }

      releasePageRef(config._otPageRef);

      // Record http.client.request.duration
      if (config._otStartTime) {
        const duration = (performance.now() - config._otStartTime) / 1000;
        httpClientRequestDuration?.record(duration, {
          'http.request.method': method,
          'http.response.status_code': statusCode,
          'http.route': urlPath,
          'url.path': urlPath,
          'url.scheme': _.replace(window.location.protocol, ':', '')
        });
      }

      return response;
    },
    (error: any) => {
      const config = error.config as AxiosConfigWithSpan;
      const span = config?._otSpan;
      const responseStatus = error.response?.status;
      const isServerError = !error.response || responseStatus >= 500;

      if (span) {
        if (isServerError) {
          span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
          span.setAttribute(ATTR_ERROR_TYPE, error.name || 'Error');
          span.recordException(error);
        } else {
          span.setStatus({ code: SpanStatusCode.OK });
        }

        if (error.response) {
          span.setAttribute(ATTR_HTTP_RESPONSE_STATUS_CODE, responseStatus);
        }

        span.end();
      }

      releasePageRef(config?._otPageRef);

      const errMethod = _.toUpper(config?.method) || 'GET';
      const errStatusCode = error.response?.status || 0;
      const errRoute = config ? parameterizePath(resolveUrl(config.url, config.baseURL).pathname) : 'unknown';

      apiErrorCounter?.add(1, {
        'http.request.method': errMethod,
        'http.response.status_code': errStatusCode,
        'error.type': error.name || 'Error',
        'url.path': errRoute
      });

      // Record http.client.request.duration
      if (config?._otStartTime) {
        const duration = (performance.now() - config._otStartTime) / 1000;
        httpClientRequestDuration?.record(duration, {
          'http.request.method': errMethod,
          'http.response.status_code': errStatusCode,
          'http.route': errRoute,
          'url.path': errRoute,
          'url.scheme': _.replace(window.location.protocol, ':', '')
        });
      }

      return Promise.reject(error);
    }
  );
};
